<template>
  <!-- Split View Mode -->
  <div v-if="hasSplitView" class="h-full flex flex-col">
    <div class="flex-none p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="flex-1 min-h-0 p-6 pt-4">
      <SplitView
        height="100%"
        :panel-count="3"
        :initial-widths="[20, 45, 35]"
        ref="splitView"
      >
        <!-- Panel 0: Email List -->
        <template #panel-0>
          <div class="h-full flex flex-col" ref="emailListPanel">
            <div class="p-4 border-b">
              <h3 class="text-lg font-medium text-gray-900">{{ $t('go.lista_mail.title') }}</h3>
            </div>
            <div>
              <div class="p-2" v-if="$page.props.isAdminOrSuperAdmin">
                {{ $t('go.lista_mail.show_filter') }}
                <select v-model="localMailFilter" @change="reloadEmails()">
                  <option value="1">{{ $t('go.lista_mail.new_unread') }}</option>
                  <option value="2">{{ $t('go.lista_mail.all_active') }}</option>
                </select>
              </div>
              <PageServerTable
                :headers="localEmailHeaders"
                :items="localEmailItems"
                v-model:items-per-page="localEmailIpp"
                :items-per-page-options="emailIppo || []"
                :sort-by="localEmailSortby"
                :items-length="localEmailCount"
                :reload-fn="reloadEmails"
                :item-value="'id'"
                :page="localEmailPage"
                show-current-page
                class="h-full go-mail-list"
              >
                <template #item.sender="{ item }">
                  <div class="break-all">
                    {{ item.sender }}
                  </div>
                </template>
                <template #item.id="{ item }">
                  <div class="flex flex-row-reverse">
                    <button
                      @click="viewEmail(item.id)"
                      class="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500"
                    >
                      {{ $t('go.actions.open') }}
                    </button>
                  </div>
                </template>
                <template #item.date="{ item }">
                  <div class="flex gap-2 flex-row-reverse">
                    {{ formatDateTime(item.date) }}
                  </div>
                </template>
              </PageServerTable>
            </div>
          </div>
        </template>

        <!-- Panel 1: Email Detail -->
        <template #panel-1>
          <div class="h-full flex flex-col" ref="emailDetailPanel">
            <div v-if="selectedEmailId" class="h-full flex flex-col">
              <div class="p-4 border-b flex items-center justify-between">
                <button
                  @click="backToList"
                  class="flex items-center text-gray-600 hover:text-gray-800"
                >
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  {{ $t('go.actions.back_to_list') }}
                </button>
                <h3 class="text-lg font-medium text-gray-900">{{ $t('go.lista_mail.mail_detail') }}</h3>
              </div>
              <div class="flex-1 overflow-auto">
                <MailRenderer
                  v-if="emailContent"
                  :mailcontent="emailContent"
                  :emlurl="emailUrl"
                  :senderEmail="senderEmail"
                  :extradata="emailextradata"
                  :attachments="emailattachments"
                  :admin="emailadmin"
                  :users="emailusers"
                  :embedded="true"
                  @create-order-from-email="handleCreateOrderFromEmail"
                  @refresh-list="reloadEmails"
                />
                <div v-else class="p-4 text-center text-gray-500">
                  {{ $t('go.lista_mail.loading') }}
                </div>
              </div>
            </div>
            <div v-else class="h-full flex items-center justify-center text-gray-400">
              {{ $t('go.lista_mail.select_email') }}
            </div>
          </div>
        </template>

        <!-- Panel 2: Order Panel -->
        <template #panel-2>
          <OrderPanel
            :user="user"
            :submodules="submodules"
            ref="orderPanel"
            @reload-mails="handleReloadEmails"
          />
        </template>
      </SplitView>
    </div>
  </div>

  <!-- Normal View Mode -->
  <div v-else>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="p-6 flex flex-col lg:flex-row items-start gap-6">
      <WelcomeUser :user="user" />
      <ModuleList :modules="links" />
    </div>
  </div>
</template>

<script>
import { Link, usePage } from '@inertiajs/vue3';
import WelcomeUser from '@/Components/WelcomeUser.vue';
import ModuleList from '@/Components/ModuleList.vue';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Layout from '@/Layouts/Layout.vue';
import SplitView from '@/Components/SplitView.vue';
import OrderPanel from '@/Components/OrderPanel.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import MailRenderer from '@/Pages/Modules/gestione-ordini/MailRenderer.vue';
import axios from 'axios';

export default {
  components: {
    Link,
    WelcomeUser,
    ModuleList,
    BreadCrumbs,
    Layout,
    SplitView,
    OrderPanel,
    PageServerTable,
    MailRenderer
  },
  computed: {
    links() {
      let entries = [];
      this.submodules.forEach(submodule => {
        entries.push({
          id: submodule.id,
          name: this.$t(submodule.name),
          slug: submodule.slug,
          icon: submodule.icon,
          href: route(submodule.route),
          active: submodule.active
        });
      });
      return entries;
    },
    navigation() {
      return [
        {
          name: 'Dashboard',
          href: route('dashboard'),
        },
        {
          name: this.$t('modules.' + this.moduleSlug),
          href: route('modules.home', this.moduleSlug),
        }
      ];
    }
  },
  props: [
    'user',
    'modules',
    'hasSplitView',
    'emailHeaders',
    'emailItems',
    'emailCount',
    'emailIppo',
    'emailSortby',
    'emailIpp',
    'emailPage',
    'submodules',
    'moduleSlug'
  ],
  data() {
    return {
      localEmailItems: this.emailItems || [],
      localEmailCount: this.emailCount || 0,
      localEmailHeaders: this.emailHeaders || [],
      localEmailSortby: this.emailSortby || [],
      localEmailIpp: this.emailIpp || 10,
      localEmailPage: this.emailPage || 1,
      localMailFilter: '1',
      selectedEmailId: null,
      emailContent: null,
      emailUrl: null,
      senderEmail: null,
      emailextradata: null,
      emailattachments: null,
      emailadmin: null,
      emailusers: null,
      emailNavigationHandler: null,
      emailInertiaBeforeHandler: null,
    };
  },
  methods: {
    reloadEmails(sortby = undefined, itemsperpage = undefined, page = undefined) {
      if (sortby) this.localEmailSortby = sortby;
      if (itemsperpage) this.localEmailIpp = itemsperpage;
      if (page) this.localEmailPage = page;

      const requestData = {
        headers: this.localEmailHeaders,
        sortby: this.localEmailSortby,
        itemsperpage: this.localEmailIpp,
        page: this.localEmailPage,
        filter: this.localMailFilter
      };

      axios.post(route('modules.ordini.listamail'), requestData, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          'X-Inertia': 'true',
          'X-Inertia-Version': usePage().version ?? ''
        }
      }).then(response => {
        const props = response.data.props;
        this.localEmailItems = props.items || props.emailItems;
        this.localEmailCount = props.count || props.emailCount;
        this.localEmailHeaders = props.headers || props.emailHeaders;
        this.localEmailSortby = props.sortby || props.emailSortby;
        this.localEmailIpp = props.ipp || props.emailIpp;
        this.localEmailPage = props.page || props.emailPage;
      }).catch(error => {
        console.error('Error reloading emails:', error);
      });
    },

    viewEmail(emailId) {
      this.selectedEmailId = emailId;
      this.loadEmailContent(emailId);

      const url = new URL(window.location);
      url.searchParams.set('mail', emailId);
      window.history.pushState({}, '', url);
    },

    backToList() {
      this.reloadEmails();
      this.selectedEmailId = null;
      this.emailContent = null;
      this.emailUrl = null;
      this.senderEmail = null;
      this.emailextradata = null;
      this.emailattachments = null;
      this.emailadmin = null;
      this.emailusers = null;

      const url = new URL(window.location);
      url.searchParams.delete('mail');
      window.history.pushState({}, '', url);
    },

    async loadEmailContent(emailId) {
      try {
        const response = await axios.get(route('modules.ordini.mail-content', emailId));

        if (response.data.success) {
          this.emailContent = response.data.mailcontent;
          this.emailUrl = response.data.emlurl;
          this.senderEmail = response.data.senderEmail;
          this.emailextradata = response.data.extradata;
          this.emailattachments = response.data.attachments;
          this.emailadmin = response.data.admin;
          this.emailusers = response.data.users;
        } else {
          console.error('Error loading email:', response.data.error);
          this.emailContent = '<p class="text-red-500">Errore nel caricamento dell\'email</p>';
        }
      } catch (error) {
        console.error('Error loading email:', error);
        this.emailContent = '<p class="text-red-500">Errore nel caricamento dell\'email</p>';
      }
    },

    handleCreateOrderFromEmail(data) {
      this.$refs.splitView.setPanelWidths([10, 40, 50]);
      if (this.$refs.orderPanel) {
        this.$refs.orderPanel.createOrderFromEmail(data);
      }
    },

    handleReloadEmails() {
      this.backToList();
    },

    setupEmailNavigationInterception() {
      this.emailNavigationHandler = (e) => {
        const link = e.target.closest('a[href]');

        const inEmailArea = link && (
          (this.$refs.emailListPanel && this.$refs.emailListPanel.contains(link)) ||
          (this.$refs.emailDetailPanel && this.$refs.emailDetailPanel.contains(link))
        );

        if (!inEmailArea) return;

        const href = link.getAttribute('href');

        const shouldIntercept = href && (
          href.includes('/go/listamail') ||
          href.includes('/go/mail/') ||
          href.includes('/go/mail-content/') ||
          href.includes('/go/deletemail') ||
          href.includes('/go/mailattachment/')
        );

        if (shouldIntercept) {
          e.preventDefault();
          e.stopPropagation();

          if (href.includes('/go/listamail')) {
            this.reloadEmails();
          } else if (href.includes('/go/mail/') && !href.includes('/go/mail-content/')) {
            const match = href.match(/\/go\/mail\/([^?]+)/);
            const emailId = match ? match[1] : null;
            if (emailId) {
              this.viewEmail(emailId);
            }
          }
        }
      };

      document.addEventListener('click', this.emailNavigationHandler, true);
    },

    cleanupEmailNavigationInterception() {
      if (this.emailNavigationHandler) {
        document.removeEventListener('click', this.emailNavigationHandler, true);
      }
    },

    setupEmailInertiaInterception() {
      this.emailInertiaBeforeHandler = (event) => {
        const url = String(event.detail?.visit?.url || '');

        if (url.includes('/go/')) {
          if (
            url.includes('/go/listamail') ||
            url.includes('/go/mail/') ||
            url.includes('/go/mail-content/') ||
            url.includes('/go/deletemail') ||
            url.includes('/go/mailattachment/')
          ) {
            event.preventDefault();
          }
        }
      };

      document.addEventListener('inertia:before', this.emailInertiaBeforeHandler);
    },

    cleanupEmailInertiaInterception() {
      if (this.emailInertiaBeforeHandler) {
        document.removeEventListener('inertia:before', this.emailInertiaBeforeHandler);
      }
    }
  },

  watch: {
    emailHeaders: {
      handler(newHeaders) {
        if (newHeaders) {
          this.localEmailHeaders = [...newHeaders];
        }
      },
      deep: true,
      immediate: true
    }
  },

  mounted() {
    if (this.hasSplitView) {
      const urlParams = new URLSearchParams(window.location.search);
      const mailId = urlParams.get('mail');
      if (mailId) {
        this.viewEmail(mailId);
      }

      this.setupEmailNavigationInterception();
      this.setupEmailInertiaInterception();
    }
  },

  beforeUnmount() {
    this.cleanupEmailNavigationInterception();
    this.cleanupEmailInertiaInterception();
  }
};
</script>

<style>
.go-mail-list .v-data-table__tr--mobile > td {
  grid-template-columns: 70px 1fr !important;
}
</style>
